/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import './WalletLandingPage.scss';
import '../../styles/commonPageStyles.scss';

import { ButtonCard, CarIcon, HomeIcon, Modal, Text, TitleWithUnderLine, Typeahead } from '@boi/core/lib';
import { colors, screenSize } from '@boi/core/lib/styles';
import { faSearch, faUserAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import myHome from '../../assets/home-insurance.svg';
import myTravel from '../../assets/travel-insurance.svg';
import { CognitoContext } from '../../CognitoUtils';
import { AgentScript } from '../../components/AgentScript/AgentScript';
import BoiRetrieveCard from '../../components/BoiRetrieveCard';
import { ExternalLink } from '../../components/ExternalLink/ExternalLink';
import { DivLink } from '../../components/InsuranceCard/DivLink/DivLink';
import {
  commonPageStylesIdentifier,
  DIRECT_DEBIT_STORAGE,
  HOME_QUOTE_THANK_YOU_STORAGE,
  SELECTED_HOME_QUOTE_STORAGE,
  TOGGLE_OPTIONAL_EXTRAS
} from '../../constants';
import { getBranch } from '../../constants/apiRequest/requestConstants/endpoints';
import { travelSubmitClaimUrl } from '../../constants/externalLinks';
import { items, QUOTE_REFERENCE } from '../../constants/home';
import { allInsuranceTypes, CAR_INSURANCE_TYPE, HOME_INSURANCE_TYPE } from '../../constants/insuranceTypeConstants';
import {
  ABOUT_YOU_REDIRECT,
  AGENT_LATEST_QUOTE_LOADED,
  LAST_PAGE_NAME,
  PAYMENT_SUCCESS
} from '../../constants/sessionStorage/genericStorageIdentifiers';
import {
  AGENT_SUMMARY_QUOTE_STORAGE,
  HOME_CUSTOMER_GIVEN_NAME,
  HOME_LATEST_QUOTE_STORAGE,
  HOME_RESUME_QUOTE,
  HOME_RESUME_QUOTE_OBJ
} from '../../constants/sessionStorage/homeStorageIdentifiers';
import {
  AGENT_CLICKED_REFERENCE_QUOTES,
  AGENT_SELECTED_QUOTE,
  USER_BRANCH
} from '../../constants/sessionStorage/userStorageIdentifiers';
import {
  getItemFromSessionStorage,
  isAgent,
  onClickIfEnabled,
  removeAllJourneysSessionStorage,
  removeAllStoredPolicyNumbers,
  removeFromSessionStorage,
  saveInSessionStorage,
  saveObjectInSessionStorage
} from '../../helpers';
import { isSuperAgent } from '../../helpers/AgentCheck';
import { journeyEnabled } from '../../helpers/FeatureHelper';
import { routes } from '../../routes';
import { formatResponse, getLatestHomeQuote } from '../../services/latestHomeQuoteService';
import { getBranchData, updateUserProfileBranch } from '../../services/profile/profile';
import type { UserProfileType } from '../../types';
import UserContext from '../../UserContext';
import { ScriptContent } from './ScriptContent';

type WalletLandingPageProps = {
  userProfile: UserProfileType,
  showBanner: boolean
};

// eslint-disable-next-line complexity
const WalletLandingPage = ({
                             userProfile,
                             showBanner
                           }: WalletLandingPageProps) => {
  const [, setIsDesktop] = useState(false);
  const [showResume] = useState(showBanner);
  const [showLogin, setShowLogin] = useState(true);
  const { userDispatch, agentScriptPending } = useContext(UserContext);
  const { cognitoUser, feature, branchList } = useContext(CognitoContext);
  const [showModal, setShowModal] = useState(false);
  const [showBranch, setShowBranch] = useState({});
  const [numEnabledJourneys, setNumEnabledJourneys] = useState(0);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  type Item = {
    id: number;
    label?: string;
    content: string[];
  };
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  const handleItemClick = (item: any) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setOpen(false);

  };

  const handlePrimaryCTA = () => {
    history.push('/wallet/getquote/aboutYou');
    setSelectedItem(null);
  };

  useEffect(() => {
    // check if its a desktop screen
    const desktop = window.innerWidth > screenSize.minDesktopWidth;
    setIsDesktop(desktop);

    if (cognitoUser !== null) {
      setShowLogin(false);
      if (cognitoUser?.attributes && cognitoUser?.attributes.given_name) {
        userDispatch({
          type: 'setFirstName',
          payload: { firstName: cognitoUser?.attributes.given_name }
        });
      }
    }
    removeAllJourneysSessionStorage();
    removeAllStoredPolicyNumbers();
  }, [cognitoUser]);

  const saveLatestQuote = async (date: string) => {
    const latest = await getLatestHomeQuote();
    const data = formatResponse(latest.home_quote);
    const extraQuestionsValues = {
      listed_or_protected:
      latest.home_quote.cover_details.risk_address.listed_or_protected,
      operational_locks:
      latest.home_quote.cover_details.risk_address.operational_locks,
      roof_tile: latest.home_quote.cover_details.risk_address.roof_tile,
      un_occupied: latest.home_quote.cover_details.risk_address.un_occupied
    };
    const value = {
      aboutYouObj: data.aboutYou,
      yourHomeObj: data.yourHome,
      yourCoverObj: {
        ...data.yourCover,
        policyStart: format(new Date(date), 'YYYY-MM-DD')
      },
      assumptions: latest.home_quote.assumptions,
      extraQuestionsValues: extraQuestionsValues
    };
    saveInSessionStorage(HOME_RESUME_QUOTE_OBJ, JSON.stringify(value));
    saveInSessionStorage(HOME_QUOTE_THANK_YOU_STORAGE, JSON.stringify(value));
  };

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (userProfile) {
      setNumEnabledJourneys(
        allInsuranceTypes.filter(journeyEnabled(feature.flags)).length
      );
      if (userProfile?.quote) {
        let quote = userProfile.quote;
        saveObjectInSessionStorage(SELECTED_HOME_QUOTE_STORAGE, quote);
        const flag = JSON.parse(getItemFromSessionStorage(HOME_RESUME_QUOTE));
        if (flag || flag === null) {
          saveInSessionStorage(HOME_RESUME_QUOTE, false);
        }
        let quoteDetails = {
          price: quote.cover_premium,
          quoteReference: quote.quote_reference,
          monthlyPrice: quote.direct_debit_amount,
          insurerType: quote.insurer
        };

        saveObjectInSessionStorage(
          `${HOME_INSURANCE_TYPE}${DIRECT_DEBIT_STORAGE}`,
          quoteDetails
        );
        saveLatestQuote(quote.start_date);
      }
    }
  }, [userProfile]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (
      (getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES) ||
        getItemFromSessionStorage(AGENT_SELECTED_QUOTE)) &&
      isAgent()
    ) {
      getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)
        ? removeFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)
        : removeFromSessionStorage(AGENT_SELECTED_QUOTE);
    }
    if (isAgent()) {
      if (getItemFromSessionStorage(USER_BRANCH) === null) {
        getBranchData(getBranch)
          .then((response: any) => {
            if (!response?.error) {
              saveInSessionStorage(USER_BRANCH, JSON.stringify(response));
              userDispatch({
                type: 'setBranchName',
                payload: { branchName: response }
              });
            } else {
              setShowModal(true);
            }
          })
          .catch((error: any) => {
            console.error(error);
          });
      } else {
        const userBranch = JSON.parse(getItemFromSessionStorage(USER_BRANCH));
        userDispatch({
          type: 'setBranchName',
          payload: { branchName: userBranch }
        });
      }
    }
    removeFromSessionStorage(QUOTE_REFERENCE);
    removeFromSessionStorage(PAYMENT_SUCCESS);
    removeFromSessionStorage(AGENT_LATEST_QUOTE_LOADED);
    removeFromSessionStorage(HOME_LATEST_QUOTE_STORAGE);
    removeFromSessionStorage(AGENT_SUMMARY_QUOTE_STORAGE);
    removeFromSessionStorage(HOME_RESUME_QUOTE_OBJ);
    removeFromSessionStorage(HOME_RESUME_QUOTE);
    removeFromSessionStorage(TOGGLE_OPTIONAL_EXTRAS);
    removeFromSessionStorage(LAST_PAGE_NAME);
    removeFromSessionStorage(HOME_CUSTOMER_GIVEN_NAME);
  }, []);

  const getTitleText = () =>
    'The Insurance Wallet';

  const className = 'c-WalletLandingPage';

  const continueQuoteNotification =
    showResume && feature.flags?.home_resume_enabled ? (
      <BoiRetrieveCard userProfile={userProfile} />
    ) : null;

  // eslint-disable-next-line complexity
  const renderLatestQuotes = () => {
    return (
      isAgent() && (
        <div className={`${className}__latestQuotesContainer`}>
          <Link
            to={`/wallet/createAccount`}
            data-ga
            id="WalletLandingPage__agentLatestQuotesHome"
            data-testid="WalletLandingPage__agentLatestQuotesHome"
          >
            <div className={`${className}__latestQuotes`}>
              <div className={`${className}__latestQuotes__homeIcon`}>
                <HomeIcon color={colors.primaryColor} />
              </div>
              <div className={`${className}__latestQuotes__label`}>
                Create Customer Account
              </div>
            </div>
          </Link>
          {isSuperAgent(cognitoUser) && (
            <Link
              to={`/wallet/user/`}
              data-ga
              id="WalletLandingPage__SuperAgentMatchingService"
              data-testid="WalletLandingPage__SuperAgentMatchingService"
            >
              <div className={`${className}__latestQuotes`}>
                <div className={`${className}__latestQuotes__icon`}>
                  <FontAwesomeIcon
                    className={'signupIcon'}
                    icon={faUserAlt}
                    size="2x"
                    color={colors.primaryColor}
                  />
                </div>
                <div className={`${className}__latestQuotes__label`}>
                  Matching Service
                </div>
              </div>
            </Link>
          )}
          <Link
            to={`/wallet/searchQuotes/`}
            data-ga
            id="WalletLandingPage__agentLatestQuotesCar"
            data-testid="WalletLandingPage__agentLatestQuotesCar"
          >
            <div className={`${className}__latestQuotes`}>
              <div className={`${className}__latestQuotes__icon`}>
                <FontAwesomeIcon
                  className={'signupIcon'}
                  icon={faSearch}
                  size="2x"
                  color={colors.primaryColor}
                />
              </div>
              <div className={`${className}__latestQuotes__label`}>
                Search All Home Quotes
              </div>
            </div>
          </Link>

          {feature.flags?.motor_enabled === true && (<Link
            to={`/wallet/latest-quotes/${CAR_INSURANCE_TYPE}`}
            data-ga
            id="WalletLandingPage__agentLatestQuotesCar"
            data-testid="WalletLandingPage__agentLatestQuotesCar"
          >
            <div className={`${className}__latestQuotes`}>
              <div className={`${className}__latestQuotes__icon`}>
                <CarIcon color={colors.primaryColor} />
              </div>
              <div className={`${className}__latestQuotes__label`}>
                Latest Quotes
              </div>
            </div>
          </Link>)
          }
          <Link
            to={`/wallet/latest-quotes/${HOME_INSURANCE_TYPE}`}
            data-ga
            id="WalletLandingPage__agentLatestQuotesHome"
            data-testid="WalletLandingPage__agentLatestQuotesHome"
            onClick={onClickIfEnabled(!agentScriptPending)}
          >
            <div className={`${className}__latestQuotes`}>
              <div className={`${className}__latestQuotes__homeIcon`}>
                <HomeIcon
                  color={
                    agentScriptPending
                      ? colors.agentScriptingBlocked
                      : colors.primaryColor
                  }
                />
              </div>
              <div className={`${className}__latestQuotes__label`}>
                Latest Quotes
              </div>
            </div>
          </Link>
        </div>
      )
    );
  };

  const checkFlagValue = journeyEnabled(feature.flags);

  const onBranchSelected = useCallback(() => {
    if (showBranch !== '') {
      updateUserProfileBranch({ branchName: showBranch }).then(
        (response: any) => {
          if (response.branch_name) {
            userDispatch({
              type: 'setBranchName',
              payload: { branchName: response }
            });
          }
        }
      );
    }
    setShowModal(false);
  }, [showModal, showBranch]);


  const getRetrieveQuote = (journey: string) => {
    if (!showLogin) {
      history.push(journey === `HOME` ? routes.aboutYouHome.url : routes.aboutYouTravel.url);
    } else {
      saveInSessionStorage(ABOUT_YOU_REDIRECT, journey);
      history.push(routes.loginPage.url);
    }
  };

  function pushToPage(routeUrl: string) {
    history.push(routeUrl);
  }

  return (
    <div
      className={className}
      id="walletlanding"
      data-testid="walletlanding"
      aria-live="polite"
    >
      {isAgent() && (
        <AgentScript>
          <ScriptContent />
        </AgentScript>
      )}
      {!isAgent() && (
        <div className={`${className}__welcomeContainer`}>
          <div className={`${commonPageStylesIdentifier}__hideOnDesktop`}>
            <div className={`${className}__welcomeText`}>
              <h1 className={`${className}__titleText`}>{getTitleText()}</h1>
            </div>
          </div>

          <div className={`${commonPageStylesIdentifier}__showOnDesktopOnly`}>
            <div className={`${commonPageStylesIdentifier}__pageTitle`}>
              <h1 className={`${className}__titleText`}>{getTitleText()}</h1>
            </div>
          </div>

          {continueQuoteNotification}
          <div className={`${className}__welcomeSubtext`}>
            <h3>Manage your Home and Travel insurance in one place – quote, renew and manage policies online.</h3>
          </div>
        </div>
      )}
      {isAgent() && (
        <div className={`${className}__welcomeContainer`}>
          <div className={`${commonPageStylesIdentifier}`}>
            <div className={`${commonPageStylesIdentifier}__pageTitle`}>
              <TitleWithUnderLine>Customer Search</TitleWithUnderLine>
              <div className={`${className}__agentSubtext`}>
                <Text size="inherit">Select your search option below:</Text>
              </div>
              {renderLatestQuotes()}
            </div>
          </div>
        </div>
      )}
      {isAgent() && (
        <div className={`${commonPageStylesIdentifier}__pageTitle`}>
          <TitleWithUnderLine>Create a new Quote</TitleWithUnderLine>
        </div>
      )}
      <div className={`${className}__ButtonGridContainer`}>
        <div
          className={`${className}__ButtonGrid`}
          style={{
            justifyContent:
              numEnabledJourneys < 3 ? 'center' : 'space-between'
          }}
        >
          {!isAgent() && (<div className={`${className}__newBoard`}>
            <div className={`${className}__moduleHeader`}>
              <img src={myHome} alt="Home" width="50" />
              <h3>Home Insurance</h3>
            </div>
            <p className={`${className}__textP`}>What would you like to do today?</p>
            <div className={`${className}__button-row`}>
              <button className={`${className}__button`} aria-label="New Home Quote"
                      onClick={() => history.push('/wallet/getquote/aboutYou')}>New Home Quote
              </button>
              <button className={`${className}__button`} aria-label="Retrieve Quote"
                      onClick={() => getRetrieveQuote(`HOME`)}>Retrieve Quote
              </button>
              <button className={`${className}__button`} aria-label="Renew Policy"
                      onClick={() => history.push('/manage/viewMultiplePolicies/home')}>Renew Policy
              </button>
            </div>

            <div className={`${className}__menu-list`}>

              <DivLink componentName={className} aLabel={`Click to open Submit a claim`}
                       onClick={() => {
                         pushToPage(`${routes.submitClaimPageHomeEcom.url}home`);
                       }}>
                › Submit a claim
              </DivLink>
              <DivLink componentName={className} aLabel={`Click to open View Policy Documents`}
                       onClick={() => {
                         pushToPage(`${routes.viewMultiplePolicies.url}home`);
                       }}>
                › View Policy Documents
              </DivLink>
              <DivLink componentName={className} aLabel={`Click to open Sign In to manage policies`}
                       onClick={() => {
                         pushToPage(`${routes.viewMultiplePolicies.url}home`);
                       }}>
                › Sign In to manage policies
              </DivLink>

              <div className={`${className}__menu-row`}
                   role="menuitem"
                   aria-haspopup="true"
                   aria-expanded={open}
                   aria-controls="dropdown-menu"
                   aria-label="Click to open Customer Checklists, Hints & Tips menu"
                   tabIndex={0}
                   onClick={() => setOpen(!open)}
                   onKeyDown={(e: SyntheticKeyboardEvent<HTMLInputElement>) => {
                     if (e.key === 'Enter' || e.key === 'ArrowDown') {
                       setOpen(!open);
                     }
                   }}>
                {open ? '▾' : '›'} Customer Checklists, Hints & Tips
              </div>

              {open && (
                <div style={{ paddingLeft: '20px' }}>
                  {items.map((item: any) => (
                    <div className={`${className}__menu-row`}
                         key={item.id}
                         tabIndex={0}
                         aria-label={`${item.label} dropdown item`}
                         onClick={() => handleItemClick(item)}
                    >
                      › {item.label}
                    </div>
                  ))}
                </div>
              )}
              {selectedItem && (<Modal show={isModalOpen}
                                       showSecondaryButton={false}
                                       modalElementId="walletlanding"
                                       id={selectedItem.id}
                                       showCloseIcon={true}
                                       closeOnBackgroundClick={true}
                                       onCloseClick={() => setIsModalOpen(false)}
                                       showModalHeader={true}
                                       title={selectedItem.label}
                                       primaryButtonLabel='Start Your Quote'
                                       data-testid={selectedItem.id}
                                       onPrimaryButtonClick={handlePrimaryCTA}>
                  <div style={{ paddingTop: '20px'}}>
                  <ul style={{ textAlign: 'left', paddingLeft: '20px'}}>
                    {selectedItem.content.map((point: string, index: number) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                  </div>
                </Modal>
              )}

            </div>
          </div>)}

          {!isAgent() && (<div className={`${className}__newBoard`}>
            <div className={`${className}__moduleHeader`}>
              <img src={myTravel} alt="Travel" width="50" />
              <h3>Travel Insurance</h3>
            </div>
            <p className={`${className}__textP`}>What would you like to do today?</p>
            <div className={`${className}__button-row`}>
              <button className={`${className}__button`} aria-label="New Travel Quote"
                      onClick={() => history.push('/wallet/getgenericquote/travel')}>New Travel Quote
              </button>
              <button className={`${className}__button`} aria-label="Retrieve Quote"
                      onClick={() => getRetrieveQuote('TRAVEL')}>Retrieve Quote
              </button>
            </div>
            <div className={`${className}__menu-list`}>

              <ExternalLink className={`${className}__menu-row`} text="› Submit a claim" url={travelSubmitClaimUrl}
                            linkAriaLabel={'Link to open claim documentation'} />

              <DivLink componentName={className} aLabel={`Click to open View Policy Documents`}
                       onClick={() => {
                         pushToPage(`${routes.viewMultiplePolicies.url}travel`);
                       }}>
                › View Policy Documents
              </DivLink>
              <DivLink componentName={className} aLabel={`Click to open Sign In to manage policies`}
                       onClick={() => {
                         pushToPage(`${routes.viewMultiplePolicies.url}travel`);
                       }}>
                › Sign In to manage policies
              </DivLink>
              <DivLink componentName={className} aLabel={`Click to open Medical Assistance`}
                       onClick={() => {
                         pushToPage(`${routes.medicalAssistanceEcom.url}travel`);
                       }}>
                › Medical Assistance
              </DivLink>
            </div>
          </div>)}


          {isAgent() && (<div
            className={`${className}__ButtonContainer`}
            style={
              checkFlagValue(HOME_INSURANCE_TYPE)
                ? null
                : { cursor: 'not-allowed' }
            }
          >
            <Link
              style={
                checkFlagValue(HOME_INSURANCE_TYPE)
                  ? null
                  : { pointerEvents: 'none' }
              }
              to={feature.flags?.home_enabled ? '/wallet/product/home' : ''}
              data-ga
              id="WalletLandingPage__homeButtonCard"
              data-testid="WalletLandingPage__homeButtonCard"
              onClick={onClickIfEnabled(!agentScriptPending)}
            >
              <ButtonCard
                icon="home"
                text="Home"
                callInsurance={feature.flags?.home_enabled}
                color={
                  agentScriptPending ? colors.agentScriptingBlocked : undefined
                }
              />
            </Link>
          </div>)}
        </div>

        {!isAgent() && (<div>
          <span className={`${className}__login_span`}>
            <p className={`${className}__textP`}>There is a 14-day cooling off period, during which time you can withdraw from contract.</p>
          </span>
          <div className={`${className}__buttonWrapper`}>
            <button className={`${className}__center-button`} aria-label="Withdraw From Contract"
                    onClick={() => window.open('https://boi.com/withdrawal', '_blank')}>Withdraw From Contract
            </button>
          </div>
        </div>)}
      </div>
      <Modal
        show={showModal}
        showSecondaryButton={false}
        modalElementId="walletlanding"
        height="250px"
        id={'modal-branch-type'}
        showCloseIcon={false}
        title="Please Select Branch"
        data-testid="modal-branch-type"
        onPrimaryButtonClick={() => {
          onBranchSelected();
        }}
      >
        <Typeahead
          data={branchList}
          data-testid="modal-branch-text"
          id={'modal-branch-text'}
          placeholder="Search Branch Type"
          onChange={(selectedOpt: any) => {
            setShowBranch(selectedOpt);
          }}
          // $FlowFixMe
          value={showBranch?.value || ''}
        />
      </Modal>
    </div>
  );
};

export default WalletLandingPage;
